/**
 * /admin/import — Tally import wizard.
 * @file src/web/features/admin/pages/import.tsx
 *
 * A guided 3-step flow so a non-technical client always knows where they
 * are: Upload → Review → Import.
 * 1. Upload — drag/drop or pick a Tally XML export; real upload progress.
 * 2. Review — auto-detected type, counts, reconciliation, and any GAPS
 * (errors block, warnings/info are advisory) before anything is saved.
 * 3. Import — writes to Postgres with a staged progress display, then a
 * success summary.
 *
 * Uses XHR (not fetch) for the upload so we can show a true progress bar.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  UploadCloud,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Info,
  XCircle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Building2,
  ScrollText,
  Database,
  PartyPopper,
  FileSpreadsheet,
  Download,
  Laptop,
  RefreshCw,
} from 'lucide-react';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { adminFetch, adminFetchJson } from '../lib/admin-api';
import { useCompany } from '../lib/company-context';
import { useCurrentRole } from '../../auth/hooks';
import { isEntityUser } from '../../../shared/utils/roles';

interface EntityLite { id: string; name: string; status: string }

/* ── types (mirror tally-import.service.ts) ────────────────────── */

interface ImportGap { severity: 'error' | 'warning' | 'info'; label: string; detail?: string; items?: string[] }
interface ImportCheck { label: string; ok: boolean; detail?: string }
interface ImportReport {
  detectedKind: 'daybook' | 'masters' | 'mixed' | 'unknown';
  company: { name: string; guid?: string } | null;
  counts: { groups: number; ledgers: number; vouchers: number; entries: number };
  reconciliation: { debits: number; credits: number; balanced: boolean } | null;
  checks: ImportCheck[];
  gaps: ImportGap[];
  valid: boolean;
}
interface LoadResult { vouchers: number; entries: number; ledgers: number; groups: number }

type Phase = 'idle' | 'uploading' | 'analyzing' | 'review' | 'importing' | 'done' | 'error';

const env = (import.meta as unknown as { env: Record<string, string> }).env;
const API = env.VITE_API_URL ?? 'http://localhost:3000';

function inr(n: number): string {
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN')}`;
}

/** XHR upload with progress. Sends the raw file bytes. */
function upload(
  file: File,
  commit: boolean,
  entityId: string,
  onProgress: (p: number) => void,
): Promise<{ ok: boolean; status: number; data: any }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = `${API}/api/tally-import/upload?commit=${commit}&entityId=${encodeURIComponent(entityId)}&filename=${encodeURIComponent(file.name)}`;
    xhr.open('POST', url);
    xhr.setRequestHeader('Content-Type', 'application/xml');
    if (env.VITE_FRONTEND_KEY) xhr.setRequestHeader('X-Frontend-Key', env.VITE_FRONTEND_KEY);
    const token = localStorage.getItem('auth_token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      let data: any = null;
      try { data = JSON.parse(xhr.responseText); } catch { /* non-JSON */ }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

const STEPS = ['Upload', 'Review', 'Import'] as const;

/** Hand-exported Tally XML — superseded by the desktop agent. See the note at
 *  the render site; flip to true (or drive it from an env var) to bring back. */
const SHOW_XML_UPLOAD = false;

export default function ImportPage() {
  const [params] = useSearchParams();
  const entityLogin = isEntityUser(useCurrentRole());
  const [phase, setPhase] = useState<Phase>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [loaded, setLoaded] = useState<LoadResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Import always targets one Entity. Defaults to ?entityId=, else the
  // client picked in the sidebar, else the sole entity, else picked here.
  const { entityId: sidebarEntityId } = useCompany();
  const [entities, setEntities] = useState<EntityLite[] | null>(null);
  const [entityId, setEntityId] = useState(params.get('entityId') ?? sidebarEntityId ?? '');

  useEffect(() => {
    adminFetchJson<{ items: EntityLite[] }>('/api/entities', { query: { status: 'active' } })
      .then((d) => {
        setEntities(d.items);
        setEntityId((cur) => cur || sidebarEntityId || (d.items.length === 1 ? d.items[0].id : ''));
      })
      .catch(() => setEntities([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Follow the sidebar client selection (only while nothing is in flight).
  useEffect(() => {
    if (sidebarEntityId) setEntityId(sidebarEntityId);
  }, [sidebarEntityId]);

  const stepIndex = phase === 'review' ? 1 : phase === 'importing' || phase === 'done' ? 2 : 0;

  const reset = () => {
    setPhase('idle'); setFile(null); setProgress(0);
    setReport(null); setLoaded(null); setErrorMsg(null);
  };

  const startValidate = useCallback(async (f: File) => {
    setFile(f); setErrorMsg(null); setProgress(0); setPhase('uploading');
    try {
      const res = await upload(f, false, entityId, (p) => {
        setProgress(p);
        if (p >= 1) setPhase('analyzing');
      });
      if (!res.ok || !res.data?.report) {
        setErrorMsg(res.data?.message || `Upload failed (HTTP ${res.status})`);
        setPhase('error');
        return;
      }
      setReport(res.data.report as ImportReport);
      setPhase('review');
    } catch (e: any) {
      setErrorMsg(e.message || 'Upload failed'); setPhase('error');
    }
  }, [entityId]);

  const startImport = useCallback(async () => {
    if (!file) return;
    setProgress(0); setPhase('importing');
    try {
      const res = await upload(file, true, entityId, (p) => setProgress(p));
      if (!res.ok || !res.data) {
        setErrorMsg(res.data?.message || `Import failed (HTTP ${res.status})`);
        setPhase('error');
        return;
      }
      setLoaded(res.data.load as LoadResult);
      setPhase('done');
    } catch (e: any) {
      setErrorMsg(e.message || 'Import failed'); setPhase('error');
    }
  }, [file, entityId]);

  const onPick = (f: File | undefined) => {
    if (!f) return;
    if (!/\.xml$/i.test(f.name)) { setErrorMsg('Please choose a Tally XML export (.xml).'); setPhase('error'); return; }
    startValidate(f);
  };

  return (
    <>
      <SEO title="Import from Tally" />
      <AdminPageHeader title="Import from Tally" breadcrumbs={[{ label: 'oddits' }, { label: 'Import' }]} />

      <Stepper current={stepIndex} />

      <div className="mt-8">
        {phase === 'idle' && (
          entities === null ? (
            <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-ink-300" /></div>
          ) : entities.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-16 text-center">
              <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-inkblack text-white"><Building2 className="h-6 w-6" /></span>
              <h3 className="mt-5 text-lg font-semibold text-ink-900">Create an entity first</h3>
              <p className="mx-auto mt-1.5 max-w-md text-sm text-ink-600">Tally data is imported into an entity. Create one, then come back to import its data.</p>
              {/* A client's own login cannot open Entities, so it is not sent there. */}
              {!entityLogin && <Link to="/admin/entities" className="mt-6 inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white no-underline"><Building2 className="h-4 w-4" /> Go to Entities</Link>}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-ink-200 bg-white px-4 py-3">
                <span className="text-sm font-medium text-ink-600">Import into entity</span>
                {entities.length === 1 ? (
                  <span className="inline-flex items-center gap-1.5 rounded-lg bg-ink-100 px-3 py-1.5 text-sm font-semibold text-ink-800">
                    <Building2 className="h-4 w-4 text-ink-400" /> {entities[0].name}
                  </span>
                ) : (
                  <select value={entityId} onChange={(e) => setEntityId(e.target.value)}
                    className="min-w-[12rem] rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter">
                    <option value="">Select an entity…</option>
                    {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                )}
              </div>
              {entityId ? (
                <>
                  <p className="text-sm text-ink-500">Bring in the books your way — the Oddits Sync desktop app, or a spreadsheet.</p>
                  {/* The Tally XML upload is hidden, not deleted. Hand-exported
                      XML is the thing the desktop agent exists to replace, and
                      leaving it on offer invites a stale one-off export next to
                      a live sync. The whole upload path — route, parser,
                      validation — is untouched behind this flag, so a client
                      who genuinely cannot run the agent can be switched back on
                      without rebuilding it. */}
                  <div className="grid items-stretch gap-4 lg:grid-cols-2">
                    <OddSyncCard />
                    <ExcelImportCard entityId={entityId} />
                    {SHOW_XML_UPLOAD && <TallyCard dragging={dragging} setDragging={setDragging} onPick={onPick} inputRef={inputRef} />}
                  </div>
                </>
              ) : (
                <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-12 text-center text-sm text-ink-500">
                  Select an entity above to choose how to import its data.
                </div>
              )}
            </div>
          )
        )}

        {(phase === 'uploading' || phase === 'analyzing') && (
          <UploadingCard file={file} progress={progress} analyzing={phase === 'analyzing'} />
        )}

        {phase === 'review' && report && (
          <Review
            report={report}
            file={file}
            onBack={reset}
            onImport={startImport}
          />
        )}

        {phase === 'importing' && <ImportingCard progress={progress} kind={report?.detectedKind} />}

        {phase === 'done' && <DoneCard loaded={loaded} kind={report?.detectedKind} onAnother={reset} />}

        {phase === 'error' && <ErrorCard message={errorMsg} onRetry={reset} />}
      </div>
    </>
  );
}

/* ── Excel/CSV import (#8) ─────────────────────────────────────── */

function ExcelImportCard({ entityId }: { entityId: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [result, setResult] = useState<{ vouchers: number; ledgers: number; entries: number } | null>(null);
  const [errors, setErrors] = useState<string[] | null>(null);

  async function downloadTemplate() {
    try {
      const res = await adminFetch('/api/tally-import/excel-template');
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'oddits-gst-template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  }

  async function onFile(file: File) {
    setBusy(true); setResult(null); setErrors(null);
    try {
      const res = await adminFetch(`/api/tally-import/excel?entityId=${encodeURIComponent(entityId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: file,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) { setErrors(body.errors ?? [body.message || body.error || 'Import failed']); return; }
      setResult({ vouchers: body.vouchers, ledgers: body.ledgers, entries: body.entries });
    } catch (e) {
      setErrors([e instanceof Error ? e.message : 'Import failed']);
    } finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  }

  return (
    <div className="flex flex-col rounded-2xl border border-ink-200 bg-white p-5">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="grid size-9 place-items-center rounded-xl bg-ok-50 text-ok-600"><FileSpreadsheet className="h-5 w-5" /></span>
          <div>
            <p className="text-sm font-semibold text-ink-900">Import from Excel / CSV</p>
            <p className="text-xs text-ink-500">One row per invoice line — no Tally needed</p>
          </div>
        </div>
        <button onClick={downloadTemplate} className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-ink-300 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 transition hover:bg-ink-50">
          <Download className="h-3.5 w-3.5" /> Template
        </button>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
        onClick={() => ref.current?.click()}
        className={`flex flex-1 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors ${
          drag ? 'border-ok-400 bg-ok-50/50' : 'border-ink-300 bg-ink-50/50 hover:border-ink-400 hover:bg-ink-50'
        }`}
      >
        <span className="grid size-12 place-items-center rounded-2xl bg-gradient-to-br from-ok-500 to-teal-600 text-white">
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <UploadCloud className="h-6 w-6" />}
        </span>
        <p className="mt-4 text-sm font-semibold text-ink-900">{busy ? 'Importing…' : 'Drop .xlsx / .csv here, or click to browse'}</p>
        <p className="mt-1 text-xs text-ink-500">Grab the template first if you need the columns</p>
        <input ref={ref} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
      </div>

      {result && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-ok-200 bg-ok-50/60 px-4 py-3 text-sm text-ok-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /><span>Imported <b>{result.vouchers}</b> vouchers, {result.entries} entries, {result.ledgers} new ledgers. Open <b>GST</b> to verify.</span>
        </div>
      )}
      {errors && (
        <div className="mt-4 rounded-xl border border-danger-200 bg-danger-50/60 px-4 py-3 text-sm text-danger-700">
          <div className="mb-1 flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" /> Import problems</div>
          <ul className="list-disc space-y-0.5 pl-5 text-xs">{errors.slice(0, 12).map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

/* ── stepper ───────────────────────────────────────────────────── */

function Stepper({ current }: { current: number }) {
  return (
    <div className="flex items-center">
      {STEPS.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={label} className="flex flex-1 items-center last:flex-none">
            <div className="flex items-center gap-2.5">
              <span
                className={`grid size-8 place-items-center rounded-full text-sm font-semibold transition-colors ${
                  done ? 'bg-ok-500 text-white'
                    : active ? 'bg-inkblack text-white'
                    : 'bg-ink-100 text-ink-400'
                }`}
              >
                {done ? <CheckCircle2 className="h-4.5 w-4.5" /> : i + 1}
              </span>
              <span className={`text-sm font-medium ${active || done ? 'text-ink-900' : 'text-ink-400'}`}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`mx-4 h-px flex-1 ${done ? 'bg-ok-400' : 'bg-ink-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── step 1 · option C: Oddits Sync desktop app (placeholder) ──── */

function OddSyncCard() {
  return (
    <div className="flex flex-col rounded-2xl border border-ink-200 bg-white p-5">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="grid size-9 place-items-center rounded-xl bg-highlighter-40 text-inkblack"><Laptop className="h-5 w-5" /></span>
          <div>
            <p className="text-sm font-semibold text-ink-900">Oddits Sync Desktop</p>
            <p className="text-xs text-ink-500">Auto-sync Tally from the client's PC</p>
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-ok-50 px-2.5 py-1 text-[11px] font-semibold text-ok-700">Recommended</span>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center rounded-xl border-2 border-dashed border-ink-200 bg-ink-50/50 px-6 py-10 text-center">
        <span className="grid size-12 place-items-center rounded-2xl bg-inkblack text-highlighter"><RefreshCw className="h-6 w-6" /></span>
        <p className="mt-4 text-sm font-semibold text-ink-900">Push Tally automatically</p>
        <p className="mt-1 text-xs text-ink-500">Runs on the client's PC and sends vouchers &amp; masters straight to oddits — no exporting XML by hand.</p>
        <Link
          to="/download"
          target="_blank"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-paper no-underline transition hover:bg-inkblack-80"
        >
          <Download className="h-4 w-4" /> Get the desktop app
        </Link>
      </div>

      <div className="mt-4 rounded-xl border border-ink-200 bg-ink-50/50 px-4 py-3 text-[11px] text-ink-500">
        The download page is public — send the client the link and they can install it themselves.
      </div>
    </div>
  );
}

/* ── step 1 · option A: import from Tally ──────────────────────── */

function TallyCard({
  dragging, setDragging, onPick, inputRef,
}: {
  dragging: boolean; setDragging: (b: boolean) => void;
  onPick: (f: File | undefined) => void; inputRef: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div className="flex flex-col rounded-2xl border border-ink-200 bg-white p-5">
      <div className="mb-4 flex items-center gap-2.5">
        <span className="grid size-9 place-items-center rounded-xl bg-highlighter-40 text-inkblack"><FileText className="h-5 w-5" /></span>
        <div>
          <p className="text-sm font-semibold text-ink-900">Import from Tally</p>
          <p className="text-xs text-ink-500">Day Book or Chart of Accounts, exported as XML</p>
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); onPick(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`flex flex-1 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors ${
          dragging ? 'border-inkblack bg-highlighter-40/50' : 'border-ink-300 bg-ink-50/50 hover:border-ink-400 hover:bg-ink-50'
        }`}
      >
        <span className="grid size-12 place-items-center rounded-2xl bg-inkblack text-white">
          <UploadCloud className="h-6 w-6" />
        </span>
        <p className="mt-4 text-sm font-semibold text-ink-900">Drop Tally XML here, or click to browse</p>
        <p className="mt-1 text-xs text-ink-500">up to 100 MB</p>
        <input ref={inputRef} type="file" accept=".xml,text/xml,application/xml" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
      </div>

      <details className="mt-4 rounded-xl border border-ink-200 bg-ink-50/50 px-4 py-3">
        <summary className="cursor-pointer select-none text-xs font-semibold text-ink-700">How to export from Tally</summary>
        <ul className="mt-3 space-y-1.5 text-xs text-ink-600">
          <li>1. Open the company, set the full year (<span className="font-medium">Alt+F2</span> → 1-Apr to 31-Mar).</li>
          <li>2. <span className="font-medium">Day Book</span> → <span className="font-medium">Alt+E</span> → format <span className="font-medium">XML</span> → export the vouchers.</li>
          <li>3. <span className="font-medium">Chart of Accounts</span> → <span className="font-medium">Alt+E</span> → <span className="font-medium">XML</span> → export the masters.</li>
          <li>4. Upload each file here — we auto-detect which is which.</li>
        </ul>
      </details>
    </div>
  );
}

/* ── step 1b: uploading ────────────────────────────────────────── */

function UploadingCard({ file, progress, analyzing }: { file: File | null; progress: number; analyzing: boolean }) {
  const pct = Math.round(progress * 100);
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <div className="flex items-center gap-3">
        <span className="grid size-11 place-items-center rounded-xl bg-highlighter-40 text-inkblack">
          <FileText className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink-900">{file?.name}</p>
          <p className="text-xs text-ink-500">{file ? `${(file.size / 1e6).toFixed(1)} MB` : ''}</p>
        </div>
        <span className="text-sm font-semibold tabular-nums text-ink-700">{analyzing ? '' : `${pct}%`}</span>
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-ink-100">
        <div
          className="h-full rounded-full bg-inkblack transition-all duration-200"
          style={{ width: `${analyzing ? 100 : pct}%` }}
        />
      </div>
      <p className="mt-3 flex items-center gap-2 text-sm text-ink-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        {analyzing ? 'Validating your data…' : 'Uploading…'}
      </p>
    </div>
  );
}

/* ── step 2: review ────────────────────────────────────────────── */

const KIND_LABEL: Record<string, string> = { daybook: 'Day Book (vouchers)', masters: 'Masters (ledgers & groups)', mixed: 'Masters + vouchers', unknown: 'Unrecognised' };

function Review({
  report, file, onBack, onImport,
}: { report: ImportReport; file: File | null; onBack: () => void; onImport: () => void }) {
  const [ack, setAck] = useState(false);
  const c = report.counts;
  const stats = report.detectedKind === 'masters'
    ? [['Groups', c.groups], ['Ledgers', c.ledgers]]
    : [['Vouchers', c.vouchers], ['Ledger entries', c.entries]];

  const errors = report.gaps.filter((g) => g.severity === 'error');
  const warnings = report.gaps.filter((g) => g.severity === 'warning');
  const blocked = !report.valid || errors.length > 0;
  const needsAck = !blocked && warnings.length > 0;
  const canImport = !blocked && (!needsAck || ack);

  return (
    <div className="space-y-5">
      {/* summary header */}
      <div className="rounded-2xl border border-ink-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="grid size-11 place-items-center rounded-xl bg-highlighter-40 text-inkblack"><Building2 className="h-5 w-5" /></span>
            <div>
              <p className="text-sm font-semibold text-ink-900">{report.company?.name ?? file?.name ?? 'Tally export'}</p>
              <p className="text-xs text-ink-500">{KIND_LABEL[report.detectedKind]}</p>
            </div>
          </div>
          {report.valid
            ? <span className="inline-flex items-center gap-1 rounded-full bg-ok-50 px-3 py-1 text-xs font-medium text-ok-600"><CheckCircle2 className="h-3.5 w-3.5" /> Ready to import</span>
            : <span className="inline-flex items-center gap-1 rounded-full bg-danger-50 px-3 py-1 text-xs font-medium text-danger-600"><XCircle className="h-3.5 w-3.5" /> Needs fixing</span>}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stats.map(([k, v]) => (
            <div key={k as string} className="rounded-xl bg-ink-50 px-3.5 py-3">
              <div className="text-xs text-ink-500">{k}</div>
              <div className="mt-0.5 text-xl font-bold tabular-nums text-ink-900">{(v as number).toLocaleString('en-IN')}</div>
            </div>
          ))}
          {report.reconciliation && (
            <>
              <div className="rounded-xl bg-ink-50 px-3.5 py-3">
                <div className="text-xs text-ink-500">Debits</div>
                <div className="mt-0.5 text-xl font-bold tabular-nums text-ink-900">{inr(report.reconciliation.debits)}</div>
              </div>
              <div className="rounded-xl bg-ink-50 px-3.5 py-3">
                <div className="text-xs text-ink-500">Credits</div>
                <div className="mt-0.5 text-xl font-bold tabular-nums text-ink-900">{inr(report.reconciliation.credits)}</div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* checks */}
      <div className="rounded-2xl border border-ink-200 bg-white p-6">
        <p className="text-sm font-semibold text-ink-900">Validation</p>
        <ul className="mt-4 space-y-2.5">
          {report.checks.map((ch, i) => (
            <li key={i} className="flex items-center gap-2.5 text-sm">
              {ch.ok
                ? <CheckCircle2 className="h-4 w-4 shrink-0 text-ok-500" />
                : <XCircle className="h-4 w-4 shrink-0 text-danger-500" />}
              <span className="text-ink-700">{ch.label}</span>
              {ch.detail && <span className="text-xs text-ink-400">· {ch.detail}</span>}
            </li>
          ))}
        </ul>
      </div>

      {/* gaps */}
      {report.gaps.length > 0 && (
        <div className="rounded-2xl border border-ink-200 bg-white p-6">
          <p className="text-sm font-semibold text-ink-900">Gaps &amp; notes</p>
          <div className="mt-4 space-y-3">
            {report.gaps.map((g, i) => <GapRow key={i} gap={g} />)}
          </div>
        </div>
      )}

      {/* human sign-off */}
      {blocked && (
        <div className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-800">
          These errors must be fixed in Tally before importing — the tool won&rsquo;t load data that doesn&rsquo;t reconcile.
        </div>
      )}
      {needsAck && (
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-warn-200 bg-warn-50 px-4 py-3 text-sm text-warn-900">
          <input
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            className="mt-0.5 size-4 rounded border-warn-300 text-inkblack focus:ring-inkblack"
          />
          <span>
            I&rsquo;ve reviewed the {warnings.length} note{warnings.length > 1 ? 's' : ''} above and want to import this data.
          </span>
        </label>
      )}

      {/* actions */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-2 rounded-full border border-ink-300 bg-white px-5 py-2.5 text-sm font-semibold text-ink-700 transition hover:bg-ink-50">
          <ArrowLeft className="h-4 w-4" /> Choose another file
        </button>
        <button
          onClick={onImport}
          disabled={!canImport}
          className="inline-flex items-center gap-2 rounded-full bg-inkblack px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
        >
          Import into oddits <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function GapRow({ gap }: { gap: ImportGap }) {
  const styles = {
    error: { box: 'border-danger-200 bg-danger-50', icon: <XCircle className="h-4 w-4 text-danger-500" />, text: 'text-danger-800' },
    warning: { box: 'border-warn-200 bg-warn-50', icon: <AlertTriangle className="h-4 w-4 text-warn-500" />, text: 'text-warn-800' },
    info: { box: 'border-info-200 bg-info-50', icon: <Info className="h-4 w-4 text-info-500" />, text: 'text-info-800' },
  }[gap.severity];
  return (
    <div className={`rounded-xl border px-4 py-3 ${styles.box}`}>
      <div className="flex gap-2.5">
        <span className="mt-0.5 shrink-0">{styles.icon}</span>
        <div className="min-w-0">
          <p className={`text-sm font-medium ${styles.text}`}>{gap.label}</p>
          {gap.detail && <p className="mt-0.5 text-xs text-ink-500">{gap.detail}</p>}
          {gap.items && gap.items.length > 0 && (
            <ul className="mt-1.5 flex flex-wrap gap-1.5">
              {gap.items.map((it, i) => (
                <li key={i} className="rounded-md bg-white/70 px-2 py-0.5 text-xs text-ink-600">{it}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── step 3: importing + done ──────────────────────────────────── */

function ImportingCard({ progress, kind }: { progress: number; kind?: string }) {
  const pct = Math.round(progress * 100);
  const uploaded = progress >= 1;
  const stages = [
    { label: 'Uploading file', icon: UploadCloud, done: uploaded, active: !uploaded },
    { label: 'Validating records', icon: ScrollText, done: uploaded, active: false },
    { label: 'Writing to database', icon: Database, done: false, active: uploaded },
  ];
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <div className="mb-5 h-2 overflow-hidden rounded-full bg-ink-100">
        <div className="h-full rounded-full bg-inkblack transition-all duration-200" style={{ width: `${uploaded ? 100 : pct}%` }} />
      </div>
      <ul className="space-y-3">
        {stages.map((s) => (
          <li key={s.label} className="flex items-center gap-3">
            <span className={`grid size-8 place-items-center rounded-lg ${s.done ? 'bg-ok-50 text-ok-600' : s.active ? 'bg-highlighter-40 text-inkblack' : 'bg-ink-50 text-ink-300'}`}>
              {s.done ? <CheckCircle2 className="h-4 w-4" /> : s.active ? <Loader2 className="h-4 w-4 animate-spin" /> : <s.icon className="h-4 w-4" />}
            </span>
            <span className={`text-sm ${s.done || s.active ? 'font-medium text-ink-900' : 'text-ink-400'}`}>{s.label}</span>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs text-ink-500">Importing {kind === 'masters' ? 'masters' : 'vouchers'} — this can take a few seconds for large files.</p>
    </div>
  );
}

function DoneCard({ loaded, kind, onAnother }: { loaded: LoadResult | null; kind?: string; onAnother: () => void }) {
  const items = kind === 'masters'
    ? [['Ledgers', loaded?.ledgers ?? 0], ['Groups', loaded?.groups ?? 0]]
    : [['Vouchers', loaded?.vouchers ?? 0], ['Ledger entries', loaded?.entries ?? 0]];
  return (
    <div className="rounded-2xl border border-ok-200 bg-ok-50/40 p-8 text-center">
      <span className="mx-auto grid size-14 place-items-center rounded-2xl bg-ok-500 text-white">
        <PartyPopper className="h-7 w-7" />
      </span>
      <h3 className="mt-5 text-lg font-semibold text-ink-900">Import complete</h3>
      <p className="mt-1.5 text-sm text-ink-600">Your data is validated and saved.</p>
      <div className="mx-auto mt-6 grid max-w-sm grid-cols-2 gap-3">
        {items.map(([k, v]) => (
          <div key={k as string} className="rounded-xl border border-ok-100 bg-white px-4 py-3">
            <div className="text-xs text-ink-500">{k}</div>
            <div className="mt-0.5 text-xl font-bold tabular-nums text-ink-900">{(v as number).toLocaleString('en-IN')}</div>
          </div>
        ))}
      </div>
      <div className="mt-7 flex justify-center gap-3">
        <Link to="/dashboard" className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white no-underline shadow-sm transition hover:-translate-y-0.5">
          View dashboard <ArrowRight className="h-4 w-4" />
        </Link>
        <button onClick={onAnother} className="inline-flex items-center gap-2 rounded-full border border-ink-300 bg-white px-5 py-2.5 text-sm font-semibold text-ink-700 transition hover:bg-ink-50">
          Import another file
        </button>
      </div>
    </div>
  );
}

function ErrorCard({ message, onRetry }: { message: string | null; onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-danger-200 bg-danger-50/50 p-8 text-center">
      <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-danger-500 text-white"><XCircle className="h-6 w-6" /></span>
      <h3 className="mt-4 text-lg font-semibold text-ink-900">Something went wrong</h3>
      <p className="mx-auto mt-1.5 max-w-md text-sm text-ink-600">{message ?? 'Please try again.'}</p>
      <button onClick={onRetry} className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink-300 bg-white px-5 py-2.5 text-sm font-semibold text-ink-700 transition hover:bg-ink-50">
        Try again
      </button>
    </div>
  );
}
